// The order of the first-sign-in asks on the native app (founder, build 38):
//   sign up → first-run tour → "Set up Face ID?" → Apple's notification prompt
//   → (only after a "Don't Allow") our "Notifications are off" popup.
// Since build 44 the practice cards sit between Face ID and the notification
// prompt (founder, 2026-10-11: notifications after "You got it!") — see
// holdForWelcome below. One place knows when the screen is "settled" enough
// for the next ask.
import { getBiometricAvailability, hasOptedIn, wasPrompted } from "./biometricSignIn";

// Routes where no prompt of ours may appear: auth, onboarding / Stripe return,
// admin, and the once-per-account tour (the ONE first-sign-in container).
export const QUIET_PREFIXES = ["/login", "/register", "/finish-stripe", "/stripe-rejected", "/admin", "/forgot-password", "/reset-password", "/first-run-tour"];

export const onQuietRoute = (pathname = window.location.pathname) =>
  QUIET_PREFIXES.some((p) => pathname.startsWith(p));

// Whether this phone HAS Face ID / Touch ID never changes while the app runs:
// ask the native side once, not on every poll.
let biometricAvailable: Promise<boolean> | null = null;
const hasBiometrics = () =>
  (biometricAvailable ??= getBiometricAvailability()
    .then((r) => r.available)
    .catch(() => false));

/** True once the Face ID question is out of the way for this account on this
 *  install: answered ("Set up" or "Not now"), already on, or not available. */
export async function faceIdQuestionSettled(userId: string): Promise<boolean> {
  if (hasOptedIn(userId) || wasPrompted(userId)) return true;
  return !(await hasBiometrics());
}

// SAFETY NET: notifications are how a talent hears a request at all, so the ask
// must never be lost to an ordering nicety. Once the talent has been on a
// normal screen this long, ask even if the Face ID question never got answered.
export const PUSH_ASK_MAX_WAIT_MS = 45_000;

// The practice comes first (founder, 2026-10-11: "they have to be a tiny bit
// more invested"). While the Requests page says it's on — the practice cards,
// "You got it!", the Desktop pop-up after it — the notification ask (and the
// Wallet step behind it) waits, however long they take: the first walkthrough
// took over the old 2-minute cap and got asked mid-practice. SAFETY NET, the
// same reason as above: never longer than this on a normal screen, so a
// talent who never does the practice is still asked (leaving the Requests
// page ends the hold too).
export const WELCOME_HOLD_MAX_MS = 600_000;
let welcomeHeld = false;
export const holdForWelcome = (on: boolean): void => {
  welcomeHeld = on;
};
export const welcomeHoldOn = (): boolean => welcomeHeld;

/**
 * Resolves when Apple's notification prompt may appear: off the quiet routes,
 * after the practice (holdForWelcome, at most WELCOME_HOLD_MAX_MS) and after
 * the Face ID question — or after PUSH_ASK_MAX_WAIT_MS on a normal screen,
 * whichever comes first. Rejects if `isCancelled()` turns true (the talent
 * signed out) so the caller never prompts a signed-out screen.
 */
export function waitForPushAskMoment(userId: string, isCancelled: () => boolean): Promise<void> {
  return new Promise((resolve, reject) => {
    let waitedOnNormalScreen = 0;
    let heldOnNormalScreen = 0;
    const check = async () => {
      if (isCancelled()) return reject(new Error("cancelled"));
      if (!onQuietRoute() && welcomeHeld && heldOnNormalScreen < WELCOME_HOLD_MAX_MS) {
        heldOnNormalScreen += 1000;
      } else if (!onQuietRoute()) {
        const settled = await faceIdQuestionSettled(userId).catch(() => true);
        if (settled || waitedOnNormalScreen >= PUSH_ASK_MAX_WAIT_MS) {
          // A beat after the Face ID popup closes, so the two never feel stacked.
          return window.setTimeout(() => (isCancelled() ? reject(new Error("cancelled")) : resolve()), 800);
        }
        waitedOnNormalScreen += 1000;
      }
      window.setTimeout(check, 1000);
    };
    check();
  });
}

/** Test hook: forget the cached "has biometrics" answer and any hold. */
export const _resetForTests = () => {
  biometricAvailable = null;
  welcomeHeld = false;
};
